/**
 * @module services/offlineDb
 * @description IndexedDB-based offline storage for EmberPOS transactions.
 * Stores orders, payments, and other transactional data when the server API
 * is unreachable. Data is queued for sync when connectivity resumes.
 *
 * Uses IndexedDB directly (no external dependencies) since the app runs in
 * Electron's Chromium renderer or a modern browser. IndexedDB provides
 * persistent, structured storage well-suited for offline transaction queuing.
 */

const DB_NAME = 'firepos_offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  PENDING_ORDERS: 'pending_orders',
  PENDING_PAYMENTS: 'pending_payments',
  PENDING_ORDER_UPDATES: 'pending_order_updates',
  SYNC_LOG: 'sync_log',
};

let dbInstance = null;

/**
 * Opens (or creates) the IndexedDB database.
 * @returns {Promise<IDBDatabase>}
 */
function openDb() {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Pending orders: queued POST /api/orders payloads
      if (!db.objectStoreNames.contains(STORES.PENDING_ORDERS)) {
        const ordersStore = db.createObjectStore(STORES.PENDING_ORDERS, {
          keyPath: 'offlineId',
          autoIncrement: true,
        });
        ordersStore.createIndex('tableId', 'tableId', { unique: false });
        ordersStore.createIndex('createdAt', 'createdAt', { unique: false });
        ordersStore.createIndex('status', 'status', { unique: false });
      }

      // Pending payments: queued close-order payloads
      if (!db.objectStoreNames.contains(STORES.PENDING_PAYMENTS)) {
        const paymentsStore = db.createObjectStore(STORES.PENDING_PAYMENTS, {
          keyPath: 'offlineId',
          autoIncrement: true,
        });
        paymentsStore.createIndex('orderId', 'orderId', { unique: false });
        paymentsStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Pending order updates (status changes, item edits, etc.)
      if (!db.objectStoreNames.contains(STORES.PENDING_ORDER_UPDATES)) {
        const updatesStore = db.createObjectStore(STORES.PENDING_ORDER_UPDATES, {
          keyPath: 'offlineId',
          autoIncrement: true,
        });
        updatesStore.createIndex('orderId', 'orderId', { unique: false });
        updatesStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Sync log: records of successful syncs for auditing
      if (!db.objectStoreNames.contains(STORES.SYNC_LOG)) {
        const logStore = db.createObjectStore(STORES.SYNC_LOG, {
          keyPath: 'id',
          autoIncrement: true,
        });
        logStore.createIndex('syncedAt', 'syncedAt', { unique: false });
        logStore.createIndex('type', 'type', { unique: false });
      }
    };

    request.onsuccess = (event) => {
      dbInstance = event.target.result;
      resolve(dbInstance);
    };

    request.onerror = (event) => {
      console.error('[OfflineDB] Failed to open database:', event.target.error);
      reject(event.target.error);
    };
  });
}

/**
 * Generic helper: add a record to a store.
 * @param {string} storeName
 * @param {Object} data
 * @returns {Promise<number>} The auto-incremented key
 */
async function addRecord(storeName, data) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.add({
      ...data,
      createdAt: new Date().toISOString(),
    });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic helper: get all records from a store.
 * @param {string} storeName
 * @returns {Promise<Array<Object>>}
 */
async function getAllRecords(storeName) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic helper: delete a record from a store by key.
 * @param {string} storeName
 * @param {number} key
 * @returns {Promise<void>}
 */
async function deleteRecord(storeName, key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic helper: count records in a store.
 * @param {string} storeName
 * @returns {Promise<number>}
 */
async function countRecords(storeName) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.count();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ============================================
// PUBLIC API
// ============================================

/**
 * Queue an order for later sync.
 * @param {Object} orderPayload - The POST /api/orders request body
 * @returns {Promise<number>} The offline ID
 */
export async function queueOrder(orderPayload) {
  return addRecord(STORES.PENDING_ORDERS, {
    ...orderPayload,
    status: 'pending',
  });
}

/**
 * Queue a payment (close order) for later sync.
 * @param {Object} paymentPayload - The order close payload
 * @returns {Promise<number>} The offline ID
 */
export async function queuePayment(paymentPayload) {
  return addRecord(STORES.PENDING_PAYMENTS, paymentPayload);
}

/**
 * Queue an order update (status change, item edit) for later sync.
 * @param {Object} updatePayload - Contains endpoint, method, body
 * @returns {Promise<number>} The offline ID
 */
export async function queueOrderUpdate(updatePayload) {
  return addRecord(STORES.PENDING_ORDER_UPDATES, updatePayload);
}

/**
 * Get all pending (unsynced) orders.
 * @returns {Promise<Array<Object>>}
 */
export async function getPendingOrders() {
  return getAllRecords(STORES.PENDING_ORDERS);
}

/**
 * Get all pending (unsynced) payments.
 * @returns {Promise<Array<Object>>}
 */
export async function getPendingPayments() {
  return getAllRecords(STORES.PENDING_PAYMENTS);
}

/**
 * Get all pending (unsynced) order updates.
 * @returns {Promise<Array<Object>>}
 */
export async function getPendingOrderUpdates() {
  return getAllRecords(STORES.PENDING_ORDER_UPDATES);
}

/**
 * Remove a synced order from the pending queue.
 * @param {number} offlineId
 */
export async function removePendingOrder(offlineId) {
  return deleteRecord(STORES.PENDING_ORDERS, offlineId);
}

/**
 * Remove a synced payment from the pending queue.
 * @param {number} offlineId
 */
export async function removePendingPayment(offlineId) {
  return deleteRecord(STORES.PENDING_PAYMENTS, offlineId);
}

/**
 * Re-map queued payment records from a temporary offline order ID
 * to the real server order ID after sync.
 * @param {string|number} fromOrderId
 * @param {string|number} toOrderId
 * @returns {Promise<number>} Number of payment records updated
 */
export async function remapPendingPaymentsForOrder(fromOrderId, toOrderId) {
  if (!fromOrderId || !toOrderId || fromOrderId === toOrderId) {
    return 0;
  }

  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_PAYMENTS, 'readwrite');
    const store = tx.objectStore(STORES.PENDING_PAYMENTS);
    const request = store.getAll();
    let updated = 0;

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const records = request.result || [];
      records.forEach((record) => {
        if (record.orderId === fromOrderId) {
          store.put({ ...record, orderId: toOrderId });
          updated += 1;
        }
      });
    };

    tx.oncomplete = () => resolve(updated);
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Remove a synced order update from the pending queue.
 * @param {number} offlineId
 */
export async function removePendingOrderUpdate(offlineId) {
  return deleteRecord(STORES.PENDING_ORDER_UPDATES, offlineId);
}

/**
 * Record a successful sync in the log.
 * @param {string} type - 'order', 'payment', or 'order_update'
 * @param {Object} details - Sync details
 */
export async function logSync(type, details) {
  return addRecord(STORES.SYNC_LOG, {
    type,
    syncedAt: new Date().toISOString(),
    details,
  });
}

/**
 * Get the total count of all pending items across all queues.
 * @returns {Promise<number>}
 */
export async function getPendingCount() {
  const [orders, payments, updates] = await Promise.all([
    countRecords(STORES.PENDING_ORDERS),
    countRecords(STORES.PENDING_PAYMENTS),
    countRecords(STORES.PENDING_ORDER_UPDATES),
  ]);
  return orders + payments + updates;
}

/**
 * Get a summary of all pending items.
 * @returns {Promise<{orders: number, payments: number, updates: number, total: number}>}
 */
export async function getPendingSummary() {
  const [orders, payments, updates] = await Promise.all([
    countRecords(STORES.PENDING_ORDERS),
    countRecords(STORES.PENDING_PAYMENTS),
    countRecords(STORES.PENDING_ORDER_UPDATES),
  ]);
  return {
    orders,
    payments,
    updates,
    total: orders + payments + updates,
  };
}

/**
 * Clear all pending data (use after full sync or for testing).
 * @returns {Promise<void>}
 */
export async function clearAllPending() {
  const db = await openDb();
  const storeNames = [
    STORES.PENDING_ORDERS,
    STORES.PENDING_PAYMENTS,
    STORES.PENDING_ORDER_UPDATES,
  ];

  return Promise.all(
    storeNames.map(
      (storeName) =>
        new Promise((resolve, reject) => {
          const tx = db.transaction(storeName, 'readwrite');
          const store = tx.objectStore(storeName);
          const request = store.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        })
    )
  );
}

// Keyed per tenant (spec D7): a device that switches restaurant (or signs out
// to the sample tenant) never shows another restaurant's cached menu. The old
// unkeyed 'firepos_menu_cache' entry is never read.
const MENU_CACHE_KEY = 'firepos_menu_cache';
export const menuCacheKey = (tenantKey) => `${MENU_CACHE_KEY}:${tenantKey || 'sample'}`;

export async function cacheFullMenu(menuData, tenantKey) {
  try {
    localStorage.setItem(
      menuCacheKey(tenantKey),
      JSON.stringify({
        data: menuData,
        cachedAt: new Date().toISOString(),
      })
    );
  } catch {
    // localStorage quota exceeded or unavailable - ignore
  }
}

export function getCachedFullMenu(tenantKey) {
  try {
    const cached = localStorage.getItem(menuCacheKey(tenantKey));
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    return parsed && typeof parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, 'data')
      ? parsed
      : null;
  } catch {
    return null;
  }
}

export { STORES };
export default {
  queueOrder,
  queuePayment,
  queueOrderUpdate,
  getPendingOrders,
  getPendingPayments,
  getPendingOrderUpdates,
  removePendingOrder,
  removePendingPayment,
  remapPendingPaymentsForOrder,
  removePendingOrderUpdate,
  logSync,
  getPendingCount,
  getPendingSummary,
  clearAllPending,
  cacheFullMenu,
  getCachedFullMenu,
};
